import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  TrendingUp, TrendingDown, DollarSign, Car, Calendar, AlertCircle,
  ChevronDown, ChevronUp, Plus, Edit2, Trash2, Info, ExternalLink, BarChart3,
  ArrowUpRight, ArrowDownRight, Minus, PieChart, Activity, RefreshCw, Zap,
  Shield
} from 'lucide-react'
import { supabase, getAllMarketValuations, requestMarketValuation } from '../../lib/supabase'

// Simple chart component (no external dependency)
function ValueChart({ data, height = 200 }) {
  if (!data || data.length < 2) {
    return (
      <div className="flex items-center justify-center bg-gray-50 rounded-lg" style={{ height }}>
        <p className="text-gray-500 text-sm">Add more valuations to see the trend</p>
      </div>
    )
  }

  const values = data.map(d => d.value)
  const minValue = Math.min(...values) * 0.95
  const maxValue = Math.max(...values) * 1.05
  const range = maxValue - minValue

  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - ((d.value - minValue) / range) * 100,
    ...d
  }))

  const pathD = points.map((p, i) =>
    i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`
  ).join(' ')

  const areaD = `${pathD} L 100 100 L 0 100 Z`
  const trend = data[data.length - 1].value >= data[0].value ? 'up' : 'down'
  const strokeColor = trend === 'up' ? '#22c55e' : '#ef4444'
  const fillColor = trend === 'up' ? '#22c55e20' : '#ef444420'

  return (
    <div className="relative" style={{ height }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="w-full h-full"
      >
        {[0, 25, 50, 75, 100].map(y => (
          <line
            key={y}
            x1="0" y1={y} x2="100" y2={y}
            stroke="#e5e7eb" strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <path d={areaD} fill={fillColor} />
        <path
          d={pathD} fill="none" stroke={strokeColor} strokeWidth="2"
          vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round"
        />
        {points.map((p, i) => (
          <circle
            key={i} cx={p.x} cy={p.y} r="3"
            fill="white" stroke={strokeColor} strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <div className="absolute left-0 top-0 bottom-0 w-16 flex flex-col justify-between text-xs text-gray-500 -ml-16">
        <span>${(maxValue / 1000).toFixed(0)}k</span>
        <span>${((maxValue + minValue) / 2000).toFixed(0)}k</span>
        <span>${(minValue / 1000).toFixed(0)}k</span>
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        {data.length > 0 && (
          <>
            <span>{new Date(data[0].date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}</span>
            <span>{new Date(data[data.length - 1].date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}</span>
          </>
        )}
      </div>
    </div>
  )
}

// Add Valuation Modal (legacy manual entry)
function AddValuationModal({ vehicle, onClose, onSave }) {
  const [value, setValue] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [source, setSource] = useState('owner_estimate')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)

  const sources = [
    { value: 'owner_estimate', label: 'Owner Estimate' },
    { value: 'hagerty', label: 'Hagerty Valuation' },
    { value: 'bat_sale', label: 'Bring a Trailer Sale' },
    { value: 'auction', label: 'Auction Result' },
    { value: 'dealer_quote', label: 'Dealer Quote' },
    { value: 'insurance', label: 'Insurance Appraisal' },
    { value: 'professional', label: 'Professional Appraisal' },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onSave({
        vehicle_id: vehicle.id,
        value: parseFloat(value.replace(/,/g, '')),
        date,
        source,
        notes
      })
      onClose()
    } catch (error) {
      console.error('Error saving valuation:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Manual Valuation</h2>
          <p className="text-sm text-gray-500">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/[^0-9,]/g, ''))}
                placeholder="150,000"
                required
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D85C2B] focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date" value={date} onChange={(e) => setDate(e.target.value)} required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D85C2B] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={source} onChange={(e) => setSource(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D85C2B] focus:border-transparent"
            >
              {sources.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Condition notes, comparable sales, etc."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D85C2B] focus:border-transparent resize-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading || !value}
              className="flex-1 px-4 py-2 bg-[#D85C2B] text-white rounded-lg hover:bg-[#c24e22] disabled:bg-gray-300">
              {loading ? 'Saving...' : 'Save Valuation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Format large currency values
const formatValue = (val) => {
  if (!val) return '$0'
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

// Trend display helpers
const getTrendDisplay = (trend) => {
  switch (trend) {
    case 'appreciating':
      return { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', label: 'Appreciating' }
    case 'depreciating':
      return { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', label: 'Depreciating' }
    default:
      return { icon: Minus, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Stable' }
  }
}

const getConfidenceColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-50'
  if (score >= 60) return 'text-amber-600 bg-amber-50'
  return 'text-red-600 bg-red-50'
}

const getImpactDisplay = (impact) => {
  switch (impact) {
    case 'positive':
      return { icon: ArrowUpRight, color: 'text-green-600', bg: 'bg-green-50' }
    case 'negative':
      return { icon: ArrowDownRight, color: 'text-red-600', bg: 'bg-red-50' }
    default:
      return { icon: Minus, color: 'text-gray-500', bg: 'bg-gray-50' }
  }
}

const getSourceColor = (source) => {
  const colors = {
    'Bring a Trailer': 'bg-orange-50 text-orange-700',
    'Cars & Bids': 'bg-blue-50 text-blue-700',
    'Mecum Auctions': 'bg-red-50 text-red-700',
    "RM Sotheby's": 'bg-purple-50 text-purple-700',
    'Bonhams': 'bg-emerald-50 text-emerald-700',
    'Gooding & Company': 'bg-indigo-50 text-indigo-700',
    'Barrett-Jackson': 'bg-amber-50 text-amber-700'
  }
  return colors[source] || 'bg-gray-50 text-gray-700'
}

export default function Valuations() {
  const [vehicles, setVehicles] = useState([])
  const [valuations, setValuations] = useState({})
  const [marketValuations, setMarketValuations] = useState({})
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshingValuation, setRefreshingValuation] = useState(null) // vehicleId or null
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDrivers, setShowDrivers] = useState(false)
  const [showComps, setShowComps] = useState(false)
  const [valuationCounts, setValuationCounts] = useState({}) // vehicleId -> count
  const [limitError, setLimitError] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Fetch vehicles
      const { data: vehiclesData } = await supabase
        .from('vehicles')
        .select('*')
        .eq('owner_id', user.id)
        .order('year', { ascending: false })

      setVehicles(vehiclesData || [])

      if (vehiclesData?.length > 0) {
        // Fetch user-entered valuations
        const { data: valuationsData } = await supabase
          .from('vehicle_valuations')
          .select('*')
          .in('vehicle_id', vehiclesData.map(v => v.id))
          .order('date', { ascending: true })

        const grouped = (valuationsData || []).reduce((acc, val) => {
          if (!acc[val.vehicle_id]) acc[val.vehicle_id] = []
          acc[val.vehicle_id].push(val)
          return acc
        }, {})
        setValuations(grouped)

        // Fetch AI market valuations
        const { data: mvData } = await getAllMarketValuations(user.id)
        if (mvData) {
          const mvMap = {}
          mvData.forEach(mv => { mvMap[mv.vehicle_id] = mv })
          setMarketValuations(mvMap)
        }

        // Fetch valuation counts per vehicle
        const { data: countsData } = await supabase
          .from('market_valuations')
          .select('vehicle_id', { count: 'exact' })
          .in('vehicle_id', vehiclesData.map(v => v.id))

        // Group counts by vehicle_id
        const countMap = {}
        if (countsData) {
          vehiclesData.forEach(v => {
            countMap[v.id] = countsData.filter(c => c.vehicle_id === v.id).length
          })
        }
        setValuationCounts(countMap)

        setSelectedVehicle(vehiclesData[0])
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleRefreshMarketValuation(vehicle) {
    try {
      setRefreshingValuation(vehicle.id)
      setLimitError(null)
      const { data, error } = await requestMarketValuation(vehicle, true)
      if (error) throw error
      if (data) {
        setMarketValuations(prev => ({ ...prev, [vehicle.id]: data }))
        // Update count
        setValuationCounts(prev => ({ ...prev, [vehicle.id]: (prev[vehicle.id] || 0) + 1 }))
      }
    } catch (err) {
      console.error('Error refreshing valuation:', err)
      if (err.status === 429 && err.errorCode === 'limit_reached') {
        setLimitError(`You have used all 3 complimentary valuations for ${vehicle.year} ${vehicle.make} ${vehicle.model}. Contact michael@ridinganddrivingclub.com to request additional valuations.`)
      }
    } finally {
      setRefreshingValuation(null)
    }
  }

  async function saveValuation(valuation) {
    const { data, error } = await supabase
      .from('vehicle_valuations')
      .insert([valuation])
      .select()
      .single()

    if (error) throw error

    setValuations(prev => ({
      ...prev,
      [valuation.vehicle_id]: [...(prev[valuation.vehicle_id] || []), data].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      )
    }))
  }

  async function deleteValuation(id, vehicleId) {
    await supabase.from('vehicle_valuations').delete().eq('id', id)
    setValuations(prev => ({
      ...prev,
      [vehicleId]: prev[vehicleId]?.filter(v => v.id !== id) || []
    }))
  }

  // Calculate portfolio totals — prefer AI market valuations, fall back to user-entered
  const portfolioStats = useMemo(() => {
    let totalCurrentValue = 0
    let vehiclesWithData = 0
    let appreciating = 0
    let depreciating = 0
    let stable = 0

    vehicles.forEach(vehicle => {
      const mv = marketValuations[vehicle.id]
      const vehicleVals = valuations[vehicle.id] || []

      if (mv) {
        // Use AI estimated mid value
        totalCurrentValue += Number(mv.estimated_mid) || 0
        vehiclesWithData++

        if (mv.market_trend === 'appreciating') appreciating++
        else if (mv.market_trend === 'depreciating') depreciating++
        else stable++
      } else if (vehicleVals.length > 0) {
        // Fall back to latest user-entered value
        const latest = vehicleVals[vehicleVals.length - 1]
        totalCurrentValue += latest.value || 0
        vehiclesWithData++
        stable++
      }
    })

    return {
      totalValue: totalCurrentValue,
      vehiclesWithData,
      appreciating,
      depreciating,
      stable
    }
  }, [vehicles, valuations, marketValuations])

  // Selected vehicle's AI market valuation
  const selectedMV = selectedVehicle ? marketValuations[selectedVehicle.id] : null

  // Selected vehicle stats (from user-entered valuations for chart)
  const selectedStats = useMemo(() => {
    if (!selectedVehicle) return null

    const vehicleVals = valuations[selectedVehicle.id] || []
    if (vehicleVals.length === 0) return null

    const latest = vehicleVals[vehicleVals.length - 1]
    const oldest = vehicleVals[0]
    const change = latest.value - oldest.value
    const changePercent = oldest.value > 0 ? ((change / oldest.value) * 100).toFixed(1) : 0

    const values = vehicleVals.map(v => v.value)
    const maxVal = Math.max(...values)
    const minVal = Math.min(...values)

    return {
      currentValue: latest.value,
      change,
      changePercent,
      maxValue: maxVal,
      minValue: minVal,
      valueHistory: vehicleVals
    }
  }, [selectedVehicle, valuations])

  // Get a vehicle's display value (AI first, then user-entered)
  const getVehicleValue = (vehicleId) => {
    const mv = marketValuations[vehicleId]
    if (mv) return { value: Number(mv.estimated_mid), source: 'ai', trend: mv.market_trend }
    const vehicleVals = valuations[vehicleId] || []
    if (vehicleVals.length > 0) {
      return { value: vehicleVals[vehicleVals.length - 1].value, source: 'manual', trend: null }
    }
    return null
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D85C2B]"></div>
      </div>
    )
  }

  if (vehicles.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rdc-cream flex items-center justify-center">
            <TrendingUp size={28} className="text-rdc-taupe" strokeWidth={1.5} />
          </div>
          <h3 className="font-display text-lg font-semibold text-black mb-2">
            No vehicles yet
          </h3>
          <p className="text-rdc-taupe max-w-md mx-auto mb-6">
            Add a vehicle to start tracking valuations and building your Provenance Score.
          </p>
          <Link
            to="/garage/add"
            className="btn-primary inline-flex items-center gap-2 mb-4"
          >
            <Plus size={18} />
            Add Your First Vehicle
          </Link>
          <p className="text-xs text-rdc-taupe mt-4">
            Valuations are worth +15 points toward your Provenance Score
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collection Valuations</h1>
          <p className="text-gray-600 mt-1">AI-powered market estimates based on recent auction data</p>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <PieChart className="w-4 h-4" />
            <span className="text-sm">Portfolio Value</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {formatValue(portfolioStats.totalValue)}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {portfolioStats.vehiclesWithData} of {vehicles.length} vehicles valued
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Appreciating</span>
          </div>
          <div className="text-3xl font-bold text-green-600">
            {portfolioStats.appreciating}
          </div>
          <p className="text-sm text-gray-500 mt-1">vehicles trending up</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <BarChart3 className="w-4 h-4" />
            <span className="text-sm">Avg. Per Vehicle</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {portfolioStats.vehiclesWithData > 0
              ? formatValue(Math.round(portfolioStats.totalValue / portfolioStats.vehiclesWithData))
              : '$0'
            }
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Car className="w-4 h-4" />
            <span className="text-sm">Collection Size</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{vehicles.length}</div>
          <p className="text-sm text-gray-500 mt-1">vehicles in garage</p>
        </div>
      </div>

      {/* Vehicle Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-700">Select Vehicle</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              disabled={!selectedVehicle}
              className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Manual Entry
            </button>
          </div>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {vehicles.map((vehicle) => {
            const vv = getVehicleValue(vehicle.id)
            const isRefreshing = refreshingValuation === vehicle.id
            const count = valuationCounts[vehicle.id] || 0

            return (
              <button
                key={vehicle.id}
                onClick={() => {
                  setSelectedVehicle(vehicle)
                  setShowDrivers(false)
                  setShowComps(false)
                }}
                className={`flex-shrink-0 px-4 py-3 rounded-lg border-2 transition-all text-left min-w-[160px] ${
                  selectedVehicle?.id === vehicle.id
                    ? 'border-[#D85C2B] bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-sm font-medium text-gray-900">
                  {vehicle.year} {vehicle.make}
                </div>
                <div className="text-xs text-gray-500">{vehicle.model}</div>
                {vv ? (
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {formatValue(vv.value)}
                    </span>
                    {vv.source === 'ai' && vv.trend && (
                      <span className={`text-xs ${
                        vv.trend === 'appreciating' ? 'text-green-600' :
                        vv.trend === 'depreciating' ? 'text-red-600' : 'text-blue-600'
                      }`}>
                        {vv.trend === 'appreciating' ? '↑' : vv.trend === 'depreciating' ? '↓' : '→'}
                      </span>
                    )}
                    {vv.source === 'ai' && (
                      <Zap className="w-3 h-3 text-amber-500" />
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 mt-1">No valuation</div>
                )}
                {count > 0 && (
                  <div className="text-xs text-gray-400 mt-1">{count}/3 used</div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Limit Error Alert */}
      {limitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{limitError}</p>
          </div>
        </div>
      )}

      {/* Selected Vehicle — AI Market Valuation */}
      {selectedVehicle && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main valuation panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Valuation Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
                  </h3>
                  {valuationCounts[selectedVehicle.id] > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{valuationCounts[selectedVehicle.id]}/3 valuations used</p>
                  )}
                </div>
                <button
                  onClick={() => handleRefreshMarketValuation(selectedVehicle)}
                  disabled={refreshingValuation === selectedVehicle.id || valuationCounts[selectedVehicle.id] >= 3}
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#D85C2B] text-white text-sm rounded-lg hover:bg-[#c24e22] disabled:bg-gray-300"
                  title={valuationCounts[selectedVehicle.id] >= 3 ? 'Limit reached for this vehicle' : ''}
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingValuation === selectedVehicle.id ? 'animate-spin' : ''}`} />
                  {refreshingValuation === selectedVehicle.id ? 'Analyzing...' : selectedMV ? 'Refresh' : 'Generate Valuation'}
                </button>
              </div>

              {selectedMV ? (
                <>
                  {/* Value Range Display */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 mb-6 border border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Estimated Market Value</p>
                        <span className="text-4xl font-bold text-[#D85C2B]">
                          {formatValue(selectedMV.estimated_mid)}
                        </span>
                      </div>

                      {/* Trend Badge */}
                      {selectedMV.market_trend && (() => {
                        const trend = getTrendDisplay(selectedMV.market_trend)
                        const TrendIcon = trend.icon
                        return (
                          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${trend.bg}`}>
                            <TrendIcon size={16} className={trend.color} />
                            <span className={`text-sm font-semibold ${trend.color}`}>
                              {trend.label}
                              {selectedMV.trend_percentage ? ` ${selectedMV.trend_percentage > 0 ? '+' : ''}${selectedMV.trend_percentage}%` : ''}
                            </span>
                          </div>
                        )
                      })()}
                    </div>

                    {/* Range Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-2">
                        <span>Low: {formatValue(selectedMV.estimated_low)}</span>
                        <span>High: {formatValue(selectedMV.estimated_high)}</span>
                      </div>
                      <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-200 via-green-300 to-amber-200 rounded-full" />
                        <div
                          className="absolute top-0 h-full w-1 bg-[#D85C2B] rounded-full shadow-sm"
                          style={{
                            left: `${(() => {
                              const range = selectedMV.estimated_high - selectedMV.estimated_low
                              if (range === 0) return 50
                              return ((selectedMV.estimated_mid - selectedMV.estimated_low) / range) * 100
                            })()}%`,
                            transform: 'translateX(-50%)'
                          }}
                        />
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {selectedMV.confidence_score && (
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${getConfidenceColor(selectedMV.confidence_score)}`}>
                          <Shield size={12} />
                          <span className="font-medium">{selectedMV.confidence_score}% confidence</span>
                        </div>
                      )}
                      {selectedMV.created_at && (
                        <span>
                          Updated {new Date(selectedMV.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Analysis Summary */}
                  {selectedMV.analysis_summary && (
                    <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 mb-6">
                      <div className="flex items-start gap-2">
                        <Zap size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-700 leading-relaxed">{selectedMV.analysis_summary}</p>
                      </div>
                    </div>
                  )}

                  {/* Value Drivers */}
                  {selectedMV.value_drivers && selectedMV.value_drivers.length > 0 && (
                    <div className="mb-2">
                      <button
                        onClick={() => setShowDrivers(!showDrivers)}
                        className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-900 hover:text-[#D85C2B] transition-colors"
                      >
                        <span>Value Drivers ({selectedMV.value_drivers.length})</span>
                        {showDrivers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {showDrivers && (
                        <div className="space-y-2 pb-4">
                          {selectedMV.value_drivers.map((driver, i) => {
                            const impact = getImpactDisplay(driver.impact)
                            const ImpactIcon = impact.icon
                            return (
                              <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className={`w-7 h-7 rounded-full ${impact.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                  <ImpactIcon size={14} className={impact.color} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900">{driver.factor}</p>
                                  <p className="text-xs text-gray-500 leading-relaxed">{driver.description}</p>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comparable Sales */}
                  {selectedMV.comparable_sales && selectedMV.comparable_sales.length > 0 && (
                    <div className="mb-2">
                      <button
                        onClick={() => setShowComps(!showComps)}
                        className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-900 hover:text-[#D85C2B] transition-colors"
                      >
                        <span>Comparable Sales ({selectedMV.comparable_sales.length})</span>
                        {showComps ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {showComps && (
                        <div className="space-y-2 pb-4">
                          {selectedMV.comparable_sales.map((comp, i) => (
                            <div key={i} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{comp.title}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getSourceColor(comp.source)}`}>
                                      {comp.source}
                                    </span>
                                    {comp.date && (
                                      <span className="text-xs text-gray-500">{comp.date}</span>
                                    )}
                                  </div>
                                </div>
                                <span className="text-sm font-bold text-[#D85C2B] whitespace-nowrap">
                                  {formatValue(comp.price)}
                                </span>
                              </div>
                              {comp.relevance && (
                                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{comp.relevance}</p>
                              )}
                              {comp.url && (
                                <a
                                  href={comp.url} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-[#D85C2B] hover:underline mt-1"
                                >
                                  View listing <ExternalLink size={10} />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* No AI valuation yet */
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 text-center border border-gray-100">
                  <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">AI Market Valuation</h4>
                  <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                    Get an instant market estimate based on recent auction results from Bring a Trailer, Cars & Bids, Mecum, and more.
                  </p>
                  <button
                    onClick={() => handleRefreshMarketValuation(selectedVehicle)}
                    disabled={refreshingValuation === selectedVehicle.id}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#D85C2B] text-white rounded-lg hover:bg-[#c24e22] disabled:bg-gray-300"
                  >
                    {refreshingValuation === selectedVehicle.id ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Analyzing market data...
                      </>
                    ) : (
                      <>
                        <Zap size={16} />
                        Generate Valuation
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Value History Chart (from user-entered data) */}
            {selectedStats && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Your Recorded Value History</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-sm text-gray-500">Latest Entry</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatValue(selectedStats.currentValue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Change</div>
                    <div className={`text-lg font-semibold flex items-center gap-1 ${
                      selectedStats.change >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {selectedStats.change >= 0 ? (
                        <TrendingUp className="w-5 h-5" />
                      ) : (
                        <TrendingDown className="w-5 h-5" />
                      )}
                      {selectedStats.change >= 0 ? '+' : ''}{formatValue(Math.abs(selectedStats.change))}
                      <span className="text-sm">({selectedStats.changePercent}%)</span>
                    </div>
                  </div>
                </div>
                <div className="pl-16">
                  <ValueChart data={selectedStats.valueHistory} height={200} />
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — Manual Valuation History */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleRefreshMarketValuation(selectedVehicle)}
                  disabled={refreshingValuation === selectedVehicle.id}
                  className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-left text-sm transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 text-[#D85C2B] ${refreshingValuation === selectedVehicle.id ? 'animate-spin' : ''}`} />
                  <span>{selectedMV ? 'Refresh AI Valuation' : 'Generate AI Valuation'}</span>
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-left text-sm transition-colors"
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                  <span>Add Manual Valuation</span>
                </button>
                <Link
                  to={`/garage/${selectedVehicle.id}`}
                  className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-left text-sm transition-colors"
                >
                  <Car className="w-4 h-4 text-gray-600" />
                  <span>View Vehicle Details</span>
                </Link>
              </div>
            </div>

            {/* Manual Valuation History */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Recorded Valuations</h3>

              {(valuations[selectedVehicle.id] || []).length > 0 ? (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {[...(valuations[selectedVehicle.id] || [])].reverse().map((val) => (
                    <div key={val.id} className="p-3 bg-gray-50 rounded-lg group">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {formatValue(val.value)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(val.date).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-gray-400 capitalize mt-1">
                            {val.source?.replace(/_/g, ' ')}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteValuation(val.id, selectedVehicle.id)}
                          className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {val.notes && (
                        <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                          {val.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">
                  No manual valuations recorded
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Data Sources */}
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Zap className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">How AI Valuations Work</h3>
            <p className="text-sm text-gray-600 mb-4">
              Our AI analyzes recent auction results from major platforms to estimate your vehicle's current market value.
              Valuations refresh automatically every 7 days to stay current with market trends.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Bring a Trailer', 'Cars & Bids', 'Mecum Auctions', "RM Sotheby's", 'Bonhams', 'Barrett-Jackson'].map(source => (
                <span key={source} className={`text-xs font-medium px-2.5 py-1 rounded-full ${getSourceColor(source)}`}>
                  {source}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add Valuation Modal */}
      {showAddModal && selectedVehicle && (
        <AddValuationModal
          vehicle={selectedVehicle}
          onClose={() => setShowAddModal(false)}
          onSave={saveValuation}
        />
      )}
    </div>
  )
}
